/**
 * Ponto centralizado para fixes globais e otimizações
 * 
 * Este arquivo serve como ponto de entrada para todas as otimizações e polyfills
 * globais necessários para o bom funcionamento da aplicação.
 * 
 * Quando surgirem novos problemas que necessitem de correções globais,
 * elas devem ser implementadas aqui.
 */

// Lista de otimizações ativas:
// - ResizeObserver otimizado: implementado diretamente no componente AutoCompletePlus
// - Firebase Error Interceptor: filtra erros conhecidos que são falsos alarmes
// - React Error Overlay Interceptor: previne modais de erro desnecessários
// - Overlay Killer: remove fisicamente overlays de erro do Firebase
// - findDOMNode shim: compatibilidade MUI v4 com React 19

/**
 * Shim de findDOMNode para compatibilidade MUI v4 + React 19.
 * O React 19 removeu ReactDOM.findDOMNode; o MUI v4 (ButtonBase, Tooltip,
 * WithStyles) ainda depende dele internamente. Este shim restaura uma
 * implementação mínima diretamente no módulo react-dom para que todas as
 * importações — inclusive as internas do MUI (ReactDOM12) — enxerguem a
 * função, desde que resolve.dedupe garanta instância única do módulo.
 */
import * as _ReactDOM from 'react-dom';
if (typeof _ReactDOM.findDOMNode !== 'function') {
  /**
   * Percorre a árvore de fibras React para encontrar o primeiro nó DOM filho.
   * Necessário para componentes de classe (react-transition-group/Transition):
   * nesses casos, fiber.stateNode é a instância da classe, não um elemento DOM.
   * O comportamento correto de findDOMNode é retornar o primeiro DOM filho renderizado.
   */
  function _findFirstDOMInFiber(fiber) {
    if (!fiber) return null;
    if (fiber.stateNode && fiber.stateNode.nodeType) return fiber.stateNode;
    const fromChild = _findFirstDOMInFiber(fiber.child);
    if (fromChild) return fromChild;
    return _findFirstDOMInFiber(fiber.sibling);
  }

  const _findDOMNodeShim = function findDOMNode(instance) {
    if (instance == null) return null;
    if (instance.nodeType) return instance;
    if (instance._reactInternals) {
      return _findFirstDOMInFiber(instance._reactInternals);
    }
    return null;
  };

  // Object.defineProperty é safe com namespaces ESM sintéticos do Vite (deps pré-bundled).
  // Evitamos a sintaxe de atribuição direta (_ReactDOM.findDOMNode = ...)
  // pois o esbuild bloqueia estaticamente atribuições a import bindings.
  try {
    Object.defineProperty(_ReactDOM, 'findDOMNode', {
      value: _findDOMNodeShim,
      writable: true,
      configurable: true,
      enumerable: false,
    });
    console.log('[GLOBAL FIXES] findDOMNode shim instalado (React 19 + MUI v4 compat)');
  } catch (_e) { /* noop — shim não disponível neste ambiente */ }
}

import { setupReactErrorOverlayInterceptor } from './reactErrorOverlayInterceptor';
import './overlayKiller'; // Auto-executa a supressão de overlay

// Controle de mascaramento via environment
const DISABLE_ERROR_MASKING = import.meta.env.VITE_DISABLE_ERROR_MASKING === 'true';

/**
 * Intercepta erros do Firebase que são falsos alarmes
 */
const setupFirebaseErrorInterceptor = () => {
  if (DISABLE_ERROR_MASKING) {
    return;
  }

  // Intercepta erros do React Error Overlay
  if (import.meta.env.MODE === 'development') {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Filtra mensagens de erro específicas do Firebase
      const message = args.join(' ');
      if (message.includes('Missing or insufficient permissions') ||
          message.includes('FirebaseError: Missing or insufficient permissions')) {
        console.log("🔕 [GLOBAL FIXES] Erro de Firebase filtrado:", {
          originalMessage: message,
          timestamp: new Date().toISOString(),
          note: "Este erro é um falso alarme conhecido"
        });
        return; // Não exibe o erro
      }
      
      // Para outros erros, chama o console.error original
      originalConsoleError.apply(console, args);
    };
  }

  // Intercepta também window.addEventListener('error')
  const originalErrorHandler = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (typeof message === 'string' && 
        (message.includes('Missing or insufficient permissions') ||
         (error && error.message && error.message.includes('Missing or insufficient permissions')))) {
      
      console.log("🔕 [GLOBAL FIXES] Erro de window interceptado:", {
        message: message,
        source: source,
        timestamp: new Date().toISOString()
      });
      
      return true; // Previne o comportamento padrão
    }
    
    // Para outros erros, chama o handler original se existir
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    return false;
  };
};

/**
 * Aplica todas as otimizações globais
 */
export const applyGlobalFixes = () => {
  if (DISABLE_ERROR_MASKING) {
    return;
  }

  // Configura interceptador de erros do Firebase
  setupFirebaseErrorInterceptor();
  
  // Configura interceptador do React Error Overlay
  setupReactErrorOverlayInterceptor();
  
  console.log("🔧 [GLOBAL FIXES] Todos os interceptadores e supressores configurados");
};

// Executa automaticamente ao importar
applyGlobalFixes();
